import { FeatureAddOnKey, isFeatureAddOnReleased } from '@/constants';
import {
    BillingSnapshotContract,
    GatedFeatureKey,
    PlanResourceKey,
    PlanResourceLimit,
    PlanTier,
} from '@/stores/database/aliases/billing/types';
import { UserPlan } from '@/types';

export * from './catalog';

const isLegacyEnterpriseSnapshot = (snapshot?: BillingSnapshotContract | null): boolean =>
    snapshot?.plan?.code === UserPlan.ENTERPRISE;

const PLAN_TIER_ORDER: readonly PlanTier[] = ['inactive', 'free', 'plus', 'education', 'pro', 'premium', 'enterprise'];

export const getPlanTierFromUserPlan = (plan?: UserPlan): PlanTier => {
    if (plan === UserPlan.FREE) {
        return 'free';
    }

    if (plan === UserPlan.PLUS_MONTHLY || plan === UserPlan.PLUS_ANNUALLY) {
        return 'plus';
    }

    if (plan === UserPlan.EDUCATION_MONTHLY || plan === UserPlan.EDUCATION_ANNUALLY) {
        return 'education';
    }

    if (plan === UserPlan.PRO_MONTHLY || plan === UserPlan.PRO_ANNUALLY) {
        return 'pro';
    }

    if (plan === UserPlan.PREMIUM_MONTHLY || plan === UserPlan.PREMIUM_ANNUALLY) {
        return 'premium';
    }

    if (plan === UserPlan.ENTERPRISE) {
        return 'enterprise';
    }

    return 'inactive';
};

export const getResolvedPlanTier = (snapshot?: BillingSnapshotContract | null, fallbackPlan?: UserPlan): PlanTier =>
    snapshot?.entitlements?.planTier ?? getPlanTierFromUserPlan(snapshot?.plan?.code ?? fallbackPlan);

export const hasMinimumPlanAccess = (
    snapshot: BillingSnapshotContract | null | undefined,
    minimum: PlanTier,
    fallbackPlan?: UserPlan,
): boolean =>
    PLAN_TIER_ORDER.indexOf(snapshot ? getResolvedPlanTier(snapshot) : getPlanTierFromUserPlan(fallbackPlan)) >=
    PLAN_TIER_ORDER.indexOf(minimum);

export const getResolvedResourceLimit = (
    snapshot: BillingSnapshotContract | null | undefined,
    resource: PlanResourceKey,
): PlanResourceLimit | undefined => snapshot?.entitlements?.resourceLimits?.[resource];

export const isPlanEntitlementOverrideApplied = (snapshot?: BillingSnapshotContract | null): boolean => {
    if (typeof snapshot?.entitlements?.planOverrideApplied === 'boolean') {
        return snapshot.entitlements.planOverrideApplied;
    }

    return isLegacyEnterpriseSnapshot(snapshot);
};

export const hasFeatureAddOnAccess = (
    snapshot: BillingSnapshotContract | null | undefined,
    featureAddOn: FeatureAddOnKey,
): boolean => {
    if (!isFeatureAddOnReleased(featureAddOn)) {
        return false;
    }

    const resolvedAccess = snapshot?.entitlements?.addOns?.[featureAddOn];

    if (typeof resolvedAccess === 'boolean') {
        return resolvedAccess;
    }

    const legacyAccess = snapshot?.addOnIndex?.[featureAddOn];

    if (typeof legacyAccess === 'boolean') {
        return legacyAccess;
    }

    return isLegacyEnterpriseSnapshot(snapshot);
};

export const hasGatedFeatureAccess = (
    snapshot: BillingSnapshotContract | null | undefined,
    feature: GatedFeatureKey,
): boolean => {
    const resolvedAccess = snapshot?.entitlements?.features?.[feature];

    if (typeof resolvedAccess === 'boolean') {
        return resolvedAccess;
    }

    return isLegacyEnterpriseSnapshot(snapshot);
};

export const normalizeFeatureAddOnKey = (value: string): FeatureAddOnKey | undefined => {
    const normalizedValue = value.trim().toLowerCase();

    return normalizedValue === 'relations' || normalizedValue === 'lifecycles' ? normalizedValue : undefined;
};
